/**
 * NeighborLetterEntry — neighbor letters for the client to collect (docs/NEIGHBOR_LETTERS.md,
 * Matt 2026-11-03). Posted from the agency review's "Neighbor letter…" (NeighborLetterModal.jsx).
 *
 * The client prints or emails the form to each neighbor, shows them the plans, and uploads each
 * signed letter here: a PDF or a phone photo, with who signed and which box they ticked. Each one
 * becomes a Stakeholder Input on the agency review (api/_neighborLetters.js); the team hears in Slack.
 * We never contact the neighbors ourselves: the client asks them.
 *
 * entry.data: { kind: 'window' | 'support', agency, board, scope, note, pdfUrl, fileName }
 * entry.neighborLetter: { stances: [{ key, label }], returned: [{ name, address, stanceLabel, receivedOn, fileUrl }] }
 */

import { useRef, useState } from 'react'
import Markdown from '../../components/Markdown.jsx'
import { J, PrimaryButton, GhostButton, ViewOnlyNote, fmtJourneyDate } from '../journeyUI.jsx'
import JourneyBook from './JourneyBook.jsx'
import PdfPageViewer from '../../components/PdfPageViewer.jsx'
import { returnNeighborLetter } from '../../services/journey.js'

const S = {
  section: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#3a5260', margin: '26px 0 10px' },
  small: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, lineHeight: 1.6, color: '#3a5260' },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans", sans-serif', fontSize: 14, padding: '9px 12px', border: '0.5px solid rgba(7,39,45,0.25)', borderRadius: 4, color: '#07272D', background: '#fff' },
  row: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderTop: '0.5px solid rgba(7,39,45,0.1)', fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#07272D', flexWrap: 'wrap' },
  stance: on => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: on ? '0.5px solid #07272D' : '0.5px solid rgba(7,39,45,0.14)', borderRadius: 8, background: on ? '#f8fafb' : '#fff', cursor: 'pointer', marginBottom: 8, fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#07272D' }),
  err: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b', marginTop: 10 },
  link: { color: '#6b8f95', fontSize: 12, textDecoration: 'none', borderBottom: '0.5px solid rgba(7,39,45,0.22)' },
}

const ERRORS = {
  name_required: 'Please add the name of the neighbor who signed.',
  stance_required: 'Please choose the box the neighbor ticked.',
  pdf_or_photo: 'Please upload a PDF or a photo of the signed page.',
}

// An agency reads with "the" mid-sentence: "the City of Laguna Beach", "the Laguna Beach ... Planning Division".
const theAgency = (a, cap) => { const t = !a ? 'the City' : /^the /i.test(a) ? a : 'the ' + a; return cap ? t.charAt(0).toUpperCase() + t.slice(1) : t }

function downloadHref(url, name) {
  if (!url) return null
  return url + (url.includes('?') ? '&' : '?') + 'download=' + encodeURIComponent(name || 'letter.pdf')
}

export default function NeighborLetterEntry({ entry, viewOnly, token }) {
  const d = entry.data || {}
  const support = d.kind === 'support'
  const card = entry.neighborLetter || {}
  const stances = card.stances || []
  const [returned, setReturned] = useState(card.returned || [])
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [stance, setStance] = useState('')
  const [comment, setComment] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [reading, setReading] = useState(false)
  const fileRef = useRef(null)
  const what = support ? 'letter' : 'form'
  const commentStance = stances.some(s => s.key === stance) && stance !== (stances[0] && stances[0].key)

  async function send() {
    setError(null)
    if (!file) { setError('Please choose the signed ' + what + ' to upload.'); return }
    if (!/\.(pdf|jpe?g|png|heic|heif|webp)$/i.test(file.name || '')) { setError(ERRORS.pdf_or_photo); return }
    setBusy(true)
    try {
      const r = await returnNeighborLetter(token, entry.id, file, { neighborName: name.trim(), neighborAddress: address.trim(), stance, comment: comment.trim() || undefined })
      const label = (stances.find(s => s.key === stance) || {}).label || ''
      setReturned(rs => [...rs, { name: name.trim(), address: address.trim(), stanceLabel: r.stanceLabel || label, receivedOn: r.receivedOn, fileUrl: r.fileUrl }])
      setName(''); setAddress(''); setStance(''); setComment(''); setFile(null); setAdding(false)
    } catch (e) { setError(ERRORS[e.code] || e.message || 'Something went wrong. Please try again.') }
    setBusy(false)
  }

  return (
    <div>
      <div style={J.eyebrow}>For your neighbors</div>
      <h2 style={J.title}>{entry.title || (support ? 'Letters of support' : 'Neighbor input forms')}</h2>
      <hr style={J.rule} />
      <div style={J.body}>
        <p style={{ margin: 0 }}>
          {support
            ? `Your project goes before the ${d.board || 'design review board'}. The board reads letters from neighbors as part of its review.`
            : `${theAgency(d.agency, true)} would like to hear from the neighbors next to your property about ${d.scope ? d.scope.charAt(0).toLowerCase() + d.scope.slice(1) : 'the new windows and doors'}. A signed form from each of them answers the planner's question.`}
        </p>
        {d.note && <div style={{ marginTop: 14 }}><Markdown text={d.note} /></div>}
        <ol style={{ margin: '14px 0 0', paddingLeft: 20 }}>
          <li>Download the {what} below and print it, or email it to each neighbor.</li>
          <li>Show them the plans, so they know what they are signing for.</li>
          <li>Ask them to tick a box, fill in their name and address, and sign.</li>
          <li>Upload each signed {what} here: a PDF, or a photo of the page from your phone.</li>
        </ol>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', padding: '22px 0 6px' }}>
        <JourneyBook label={support ? 'Letter of support' : 'Input form'} aspect={8.5 / 11} onOpen={() => setReading(true)} downloadUrl={downloadHref(d.pdfUrl, d.fileName)} />
      </div>

      <div style={S.section}>Signed and returned{returned.length ? ` · ${returned.length}` : ''}</div>
      {!returned.length && <div style={S.small}>None yet.</div>}
      {returned.map((r, i) => (
        <div key={i} style={S.row}>
          <span><span style={{ fontWeight: 600 }}>{r.name}</span>{r.address ? <span style={{ color: '#7a9aaa' }}> · {r.address}</span> : null}</span>
          <span style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
            {r.stanceLabel && <span style={S.small}>{r.stanceLabel}</span>}
            {r.receivedOn && <span style={J.mono}>{fmtJourneyDate(r.receivedOn)}</span>}
            {r.fileUrl && <a href={r.fileUrl} target="_blank" rel="noreferrer" style={S.link}>View</a>}
          </span>
        </div>
      ))}

      {viewOnly && <div style={{ marginTop: 14 }}><ViewOnlyNote>The owner uploads the signed letters.</ViewOnlyNote></div>}

      {!viewOnly && !adding && (
        <div style={{ marginTop: 18 }}>
          <PrimaryButton onClick={() => { setAdding(true); setError(null) }}>Upload a signed {what}</PrimaryButton>
        </div>
      )}

      {!viewOnly && adding && (
        <div style={{ marginTop: 18 }}>
          <div style={S.section}>Who signed it</div>
          <input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder="Neighbor's name" aria-label="Neighbor's name" />
          <input style={{ ...S.input, marginTop: 8 }} value={address} onChange={e => setAddress(e.target.value)} placeholder="Their address" aria-label="Their address" />
          <div style={S.section}>The box they ticked</div>
          {stances.map(s => (
            <label key={s.key} style={S.stance(stance === s.key)}>
              <input type="radio" name={'nl-' + entry.id} checked={stance === s.key} onChange={() => setStance(s.key)} />
              {s.label}
            </label>
          ))}
          {commentStance && (
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} style={{ ...S.input, marginTop: 4 }} placeholder="What did they write? (optional, we will read the letter too)" aria-label="Their comments" />
          )}
          <div style={S.section}>The signed {what}</div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf,image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) setFile(f) }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <GhostButton onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>{file ? 'Choose a different file' : 'Choose file'}</GhostButton>
            {file && <span style={S.small}>{file.name}</span>}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <PrimaryButton onClick={send} disabled={busy}>{busy ? 'Uploading…' : 'Send it to us'}</PrimaryButton>
            <GhostButton onClick={() => { setAdding(false); setError(null) }} disabled={busy}>Cancel</GhostButton>
          </div>
        </div>
      )}

      {error && <div style={S.err}>{error}</div>}
      {reading && <PdfPageViewer pdfUrl={d.pdfUrl} title={entry.title || 'Letter'} downloadUrl={downloadHref(d.pdfUrl, d.fileName)} onClose={() => setReading(false)} />}
    </div>
  )
}
